import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Check,
  Building2,
  Users,
  Wrench,
  FileText,
  Bell,
  ClipboardList,
  CreditCard,
  ArrowRight,
  Smartphone,
  Shield,
  Cloud,
  Briefcase,
  Package,
  UserCheck,
  Home,
  Percent,
} from 'lucide-react';

export default function PricingPage() {
  const navigate = useNavigate();

  const includedFeatures = [
    { label: "Resident mobile app access", icon: Smartphone },
    { label: "Maintenance request tracking", icon: Wrench },
    { label: "Trustee and managing agent dashboard", icon: Shield },
    { label: "Digital document storage", icon: FileText },
    { label: "Resident notices and announcements", icon: Bell },
    { label: "Contractor quote comparison", icon: ClipboardList },
    { label: "Staff management tools", icon: UserCheck },
    { label: "Inventory and asset register", icon: Package },
    { label: "Multi-property access for owners", icon: Home },
    { label: "Secure cloud hosting", icon: Cloud },
  ];

  const paymentMethods = [
    "Secure online payments",
    "Debit or credit card",
    "Bank transfer (EFT)",
  ];

  const benefits = [
    "Improved communication between residents and trustees",
    "Transparent maintenance tracking",
    "Centralised document storage",
    "Efficient contractor management",
    "Simplified administration for managing agents",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="BodyCorp365" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold font-manrope text-slate-900">BodyCorp365</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <button 
                onClick={() => navigate('/pricing-info')}
                className="text-blue-600 font-medium"
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate('/about')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                How It Works
              </button>
              <Button variant="outline" onClick={() => navigate('/login')}>Sign In</Button>
              <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-emerald-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-manrope mb-6">
            Pricing
          </h1>
          <p className="text-xl sm:text-2xl text-white/90 mb-4">
            Simple Pricing for Residential Complexes
          </p>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            BodyCorp365 offers straightforward pricing designed for residential complexes of all sizes. 
            Our goal is to provide powerful digital management tools while keeping costs predictable and affordable.
          </p>
        </div>
      </section>

      {/* Platform Subscription Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                Platform Subscription
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-6">
                One Subscription, Full Access
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                BodyCorp365 operates on a monthly subscription per residential complex. 
                The subscription provides full access to the platform for:
              </p>
              <div className="space-y-3">
                {[
                  { icon: Users, label: "Residents and property owners" },
                  { icon: Shield, label: "Trustees and committee members" },
                  { icon: Briefcase, label: "Managing agents" },
                  { icon: UserCheck, label: "Administrative staff" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{item.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 mt-6 p-4 bg-slate-50 rounded-xl">
                All users within the registered complex can access the platform under a single subscription.
              </p>
            </div>

            {/* Pricing Card */}
            <Card className="border-2 border-blue-600 shadow-xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <p className="text-slate-500 mb-2">Starting from</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-900">R35</span>
                    <span className="text-slate-500">/unit/month</span>
                  </div>
                  <p className="text-blue-600 font-medium mt-2">Minimum R750/month</p>
                  <p className="text-slate-500 text-sm mt-1">Billed annually</p>
                </div>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                  onClick={() => navigate('/signup')}
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              What's Included
            </h2>
            <p className="text-lg text-slate-600">
              Every BodyCorp365 subscription includes:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {includedFeatures.map((feature) => (
              <div 
                key={feature.label}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-slate-700 font-medium">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contractor Marketplace Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 sm:p-12 border border-amber-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Contractor Marketplace</h2>
            </div>
            
            <p className="text-lg text-slate-600 mb-6">
              Contractors can list their services on BodyCorp365 <strong className="text-emerald-600">free of charge</strong>.
            </p>
            
            <div className="bg-white rounded-xl p-6 mb-6 border border-amber-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <Percent className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">5%</p>
                  <p className="text-slate-600">Service fee on approved quotes</p>
                </div>
              </div>
            </div>
            
            <p className="text-slate-600">
              When a contractor completes a job through the platform, a 5% service fee is applied to the approved quote. 
              This fee is paid by the managing agent as part of the contractor payment process.
            </p>
            
            <p className="text-slate-500 mt-4 italic">
              This model ensures that complexes can access a wide range of contractors without subscription barriers.
            </p>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
                <CreditCard className="w-4 h-4" />
                Payment Options
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
                Flexible Payment Methods
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                Complexes can subscribe using:
              </p>
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div key={method} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-slate-200">{method}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-400 mt-6 p-4 bg-white/5 rounded-xl">
                Invoices are available for complexes that prefer to process payments through their managing agents.
              </p>
            </div>

            <div className="flex justify-center">
              <div className="w-64 h-40 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl p-6 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform">
                <div className="flex justify-between items-start mb-8">
                  <div className="w-12 h-8 bg-amber-400 rounded"></div>
                  <div className="text-white/80 text-sm">VISA</div>
                </div>
                <div className="text-white/60 text-sm tracking-widest">•••• •••• •••• 4242</div>
                <div className="text-white/80 text-xs mt-2">BODYCORP365</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-6">
            Why Complexes Choose BodyCorp365
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            Residential communities benefit from:
          </p>
          
          <div className="grid sm:grid-cols-2 gap-4 text-left mb-12">
            {benefits.map((benefit) => (
              <div 
                key={benefit}
                className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl"
              >
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <p className="text-xl text-slate-600 mb-10">
            BodyCorp365 provides a modern digital environment for managing residential communities.
          </p>

          <div className="bg-gradient-to-r from-blue-600 to-emerald-500 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Get Started</h3>
            <p className="text-white/90 mb-6">
              Register your complex and start simplifying your property management processes today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/signup')}
              >
                Register Your Complex
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/agent-signup')}
              >
                Managing Agent Signup
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/bodycorp365-logo.svg" 
                alt="BodyCorp365" 
                className="h-8 w-auto"
              />
              <span className="text-lg font-bold text-slate-900">BodyCorp365</span>
            </div>
            <div className="flex gap-6 text-slate-600">
              <a href="/" className="hover:text-slate-900 transition-colors">Home</a>
              <a href="/#features" className="hover:text-slate-900 transition-colors">Features</a>
              <a href="/pricing-info" className="hover:text-slate-900 transition-colors">Pricing</a>
              <a href="/about" className="hover:text-slate-900 transition-colors">How It Works</a>
              <a href="/privacy" className="hover:text-slate-900 transition-colors">Privacy</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-200 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} BodyCorp365. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
